import { ProcedureSlot } from "./application-context.model";

export interface UserTypeLookup {
  description: string;
  id: string;
  name: string;
}

export interface EnumerationItem {
  displayName: string;
  value: string;
  parentValue?: string;
  children?: EnumerationItem[];
  cptCodeId?: string;
  isAuthRequired?: boolean;
}

export interface SelectableEnumerationItem extends EnumerationItem {
  isSelected: boolean;
}

export interface IcdCode {
  id: string;
  text: string;
  codeId: string;
}

/** GET `api/v1/lookup/administer-medications-list/{name}` */
export interface AdministerMedicationLookupItem {
  id: string;
  name: string;
  unit: string;
  maxValue: number;
  isActMed: boolean;
  isSedationMed: boolean;
  isBillable: boolean;
  associatedCode: string | null;
  ndcCode: string | null;
  scheduledRate: number;
  markup: number;
  billableRate: number;
  label: string;
}

export interface QuestionLookup {
  id: string;
  title: string;
  questionLabel: string;
}

export interface ChecklistQuestion {
  area: string;
  category?: string;
  id: string;
  index: number;
  label: string;
  title: string;
  answer: boolean | null;
  value?: string;
}

export interface EncounterChecklistQuestion {
  id: string;
  questionId: string;
  answer: boolean | null;
  value?: string;
}

export interface ChecklistQuestionsCache extends EncounterChecklistQuestion {
  area: string;
  index: number;
  label: string;
  title: string;
}

export interface HpiQuestions {
  id: string;
  createdAt: string;
  encounterId: string;
  question: string;
  type: string;
  response: string;
  comments: string;
  isSelected: 'Positive' | 'Negative' | 'None';
  tagName?: string;
  category?: string;
  inputType?: string;
  isMultiselect?: boolean;
  inputOptions?: any[];
  placeholder?: string;
  relevance?: string;
  purpose?: string;
  questionType?: string;
}

export interface Provider {
  id: string;
  firstName: string;
  lastName: string;
  specialty: string;
  title?: string;
  designation?: string;
  credentials?: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  state: string;
  npi: string;
}

export interface Resource {
  id: string;
  name: string;
  roleId: number;
  roleName?: string;
  title?: string;
  designation?: string;
  credentials?: string;
  /** When API supplies NPI for schedule/clinic resources (rendering provider on face sheet). */
  npi?: string;
  isSigningPhysician?: boolean;
}

export class ProcedureRoomInfo {
  name: string;
  id: string;
  noOfBeds: number;
  postProcedure: number;
  preProcedure: number;
  procedure: number;
  procedureRooms: number;
  slots: ProcedureSlot[];
  constructor() {
    this.noOfBeds = 1;
    this.postProcedure = 2;
    this.preProcedure = 2;
    this.procedure = 2;
    this.procedureRooms = 1;
    this.slots = [];
  }
}

export enum BroadcastEventTypes {
  RiskFactorsUpdated = "riskFactorsUpdated", // delete candidate
  ChiefComplaintUpdated = "chiefComplaintUpdated",
  ChiefComplaintUpdatedOnHpiCompnent = "chiefComplaintUpdatedOnHpiCompnent",
  AppointmentUpdated = "appointmentUpdated",
  PatientInfoUpdated = "patientInfoUpdated",
  MedicationSaved = "medicationSaved",
  PatientDocsLoaded = "patientDocsLoaded",
  CoverageUpdated = "coverageUpdated",
  ScheduleCacheUpdated = "scheduleCacheUpdated",
  ProcedureCacheUpdated = "procedureCacheUpdated",
  OpenReferralModal = "openReferralModal",
  RequiresProgressUpdate = "requiresProgressUpdate",
  ChecklistQuestionsUpdated = "checklistQuestionsUpdated",
  HpiQuestionsUpdated = "hpiQuestionsUpdated",
  HpiQuestionsAnswersSaved = "hpiQuestionsAnswersSaved",
  HpiQuestionsAdded = "hpiQuestionsAdded",
  HpiQuestionsGenerationStarted = "hpiQuestionsGenerationStarted",
  HpiQuestionsGenerationCompleted = "hpiQuestionsGenerationCompleted",
  HpiFindingsUpdated = "hpiFindingsUpdated",
  HpiSymptomsUpdated = "hpiSymptomsUpdated",
  HpiSymptomsGenerated = "HpiSymptomsGenerated",
  HpiSummaryGenerationStarted = "HpiSummaryGenerationStarted",
  HpiSummaryGenerationCompleted = "HpiSummaryGenerationCompleted",
  HpiSummaryGenerated = "HpiSummaryGenerated",
  RosQuestionsUpdated = "rosQuestionsUpdated",
  RosQuestionsAnswersSaved = "rosQuestionsAnswersSaved",
  RosQuestionsAdded = "rosQuestionsAdded",
  RosQuestionsGenerationStarted = "rosQuestionsGenerationStarted",
  RosQuestionsGenerationCompleted = "rosQuestionsGenerationCompleted",
  RosSymptomsUpdated = "rosSymptomsUpdated",
  RosSummaryGenerationStarted = "RosSummaryGenerationStarted",
  RosSummaryGenerationCompleted = "RosSummaryGenerationCompleted",
  PriorAuthUpdated = "priorAuthUpdated",
  LabsInfoUpdated = "labsInfoUpdated",
  AllergiesUpdated = "allergiesUpdated",
  MedicalProblemsFromDoc = "medicalProblemsFromDoc",
  MetabolicDataExtractedFromDoc = "metabolicDataExtractedFromDoc",
  PreAnesthsiaSummaryUpdated = "preAnesthsiaSummaryUpdated",
  //proceudre room event
  NeurovascularAssessmentUpdated = "neurovascularAssessmentUpdated",
  ProcedureRoomListingUpdated = "procedureRoomListingUpdated",
  SnacksAndMealsUpdated = "snacksAndMealsUpdated",
  DispenseSuppliesUpdated = "dispenseSuppliesUpdated",
  VitalsUpdated = "vitalsUpdated",
  BloodGlucoseUpdated = "bloodGlucoseUpdated",
  ModifiedAldreteScoringUpdated = "modifiedAldreteScoringUpdated",
  LeadEKGUpdated = "leadEKGUpdated",
  MedicationReconciliationUpdated = "medicationReconciliationUpdated",
  ProcedurePhiMedicationsUpdated = "procedurePhiMedicationsUpdated",
  PreProcedureAssessmentUpdated = "preProcedureAssessmentUpdated",
  ProcedureRoomNurseActivityNotesUpdated = "procedureRoomNurseActivityNotesUpdated",
  AdministerMedsUpdated = "administerMedsUpdated",
  AccessSiteUpdated = "accessSiteUpdated",
  ActsUpdated = "actsUpdated",
  /** Payload: {@link ProcedureRoomOpenPanelAddPayload} */
  ProcedureRoomOpenPanelAdd = "procedureRoomOpenPanelAdd",
  ProcedureSummaryUpdated = "procedureSummaryUpdated",
  CompleteProcedureProceedRequested = "completeProcedureProceedRequested",
  ProcedureRoomLockedUpdated = "procedureRoomLockedUpdated",
  ProcedureTimeoutUpdated = "procedureTimeoutUpdated",
  CptCodesUpdated = "cptCodesUpdated",
  IcdCodesUpdated = "icdCodesUpdated",
  ActivityNoteAdded = "activityNoteAdded",
  DischargeUpdated = "dischargeUpdated",
  //clinic v3
  EncounterProblemsUpdated = "encounterProblemsUpdated",
  EncounterProblemsVerified = "encounterProblemsVerified",
  EncounterServicesUpdated = "encounterServicesUpdated",
  EncounterContextsUpdated = "encounterContextsUpdated",
  ProcedureRoomContextsUpdated = "procedureRoomContextsUpdated",
  PlanSummaryContextUpdated = "planSummaryContextUpdated",
  AssessmentSummaryContextUpdated = "assessmentSummaryContextUpdated",
  EncounterContextTagsConfirmRequested = "encounterContextTagsConfirmRequested",
  TasksUpdated = "tasksUpdated",
  EncounterContextsStreaming = "encounterContextsStreaming",
  EncounterContextsSummaryGenerated = "EncounterContextsSummaryGenerated",
  EncounterContextsDeleted = "encounterContextsDeleted",
  EncounterMiscContextTitleValidation = "encounterMiscContextTitleValidation",
  FileSummaryGenerationStarted = "fileSummaryGenerationStarted",
  FileSummaryGenerationProgress = "fileSummaryGenerationProgress",
  FileSummaryGenerationCompleted = "fileSummaryGenerationCompleted",
  FileUploadStarted = "fileUploadStarted",
  FileUploadCompleted = "fileUploadCompleted",
  EncounterServicesAutoGeneratedUpdated = "encounterServicesAutoGeneratedUpdated",
  EncounterMedicationsUpdated = "encounterMedicationsUpdated",
  /** Plan workspace: reload clinical orders after template standing orders apply. */
  EncounterPlanOrdersUpdated = "encounterPlanOrdersUpdated",
  EncounterSoapNoteUpdated = "encounterSoapNoteUpdated",
  EncounterSoapNoteStreaming = "EncounterSoapNoteStreaming",
  EncounterServicesVerified = "encounterServicesVerified",
  EncounterMedicalHistoryUpdated = "encounterMedicalHistoryUpdated",
  EncounterSocialHistoryUpdated = "encounterSocialHistoryUpdated",
  EncounterFamilyHistoryUpdated = "encounterFamilyHistoryUpdated",
  EncounterSurgicalHistoryUpdated = "encounterSurgicalHistoryUpdated",
  EncounterModalClosed = "encounterModalClosed",
  RosSummaryUpdated = "rosSummaryUpdated",
  SuggestedServicesUpdated = "suggestedServicesUpdated",
  SuggestedProblemsUpdated = "suggestedProblemsUpdated",
  RecentAppointmentsUpdated = "recentAppointmentsUpdated",
  ReloadAppointmentDetails = "reloadAppointmentDetails",
  UpdateAppointmentDetails = "updateAppointmentDetails",
  AdmissionSelectionChanged = "admissionSelectionChanged",
  BulkAppointmentActionCompleted = "bulkAppointmentActionCompleted",
  TemplatesUpdated = "templatesUpdated",
  PqrstaQuestionsUpdated = "pqrstaQuestionsUpdated",
  OpqrstQuestionsUpdated = "opqrstQuestionsUpdated",
  PastMedicalHistoryQuestionsUpdated = "pastMedicalHistoryQuestionsUpdated",
  SocialHistoryQuestionsUpdated = "socialHistoryQuestionsUpdated",
  FamilyHistoryQuestionsUpdated = "familyHistoryQuestionsUpdated",
  HistoryQuestionsUpdated = "historyQuestionsUpdated",
  HistoryQuestionsAnswersSaved = "historyQuestionsAnswersSaved",
  HistorySummaryGenerationStarted = "historySummaryGenerationStarted",
  HistorySummaryGenerationCompleted = "historySummaryGenerationCompleted",
  HistorySummaryGenerated = "historySummaryGenerated",
  PromptsUpdated = "promptsUpdated",
  AyaMiscContextCreated = "ayaMiscContextCreated",
  SmsMessagesRefresh = "smsMessagesRefresh",
  EngageFeedRefresh = "engageFeedRefresh",
  /** Engage referral `assigned_to` / Patient Advisor cache updated (SMS join eligibility). */
  EngageReferralAssignmentChanged = "engageReferralAssignmentChanged",
  /** Refresh Engage sidebar pill counts (missed/SMS/reminders/new) after activity dismiss or realtime update. */
  EngageSidebarCountsRefresh = "engageSidebarCountsRefresh",
  /** Payload: `{ patientId: string }` — PHI patient whose encounters changed (e.g. Schedule tab saved). */
  EngageAppointmentHistoryRefresh = "engageAppointmentHistoryRefresh",
  /** Engage: unified activity feed earliest timeline instant for list date column. Payload: `{ referralId, firstActivityMs }`. */
  EngageReferralFirstActivitySynced = "engageReferralFirstActivitySynced",
  /** Engage: open the global activity/notes sidebar (e.g. when selecting a referral with pending notifications). */
  OpenGlobalNotesSidebar = "openGlobalNotesSidebar",
  /**
   * Worklist / Engage: open the global Activity sidebar for an encounter or referral-only row.
   * Payload: `{ encounterId?: string; referralId?: string; patientId?; patientName?; patientDob?; patientPhone?;
   * appliedActivityCategories?: ActivityFilterCategory[]; activityComposerMode?: 'notes' | 'sms';
   * showActivityProfileIcon?: boolean }` —
   * optional category preset (e.g. `messages` for SMS), footer composer mode, and pipeline profile shortcut.
   */
  OpenGlobalNotesForEncounter = "openGlobalNotesForEncounter",
  /**
   * Worklist: unified activity feed finished loading filtered engage activities
   * (`filter=unread_message` / `notifications`). Payload includes raw `engageActivities` for badge + mark-active.
   */
  WorksheetEngageTimelineLoaded = "worksheetEngageTimelineLoaded",
  /**
   * `/worklist/prospect`: Activity sidebar chose another referral — reload main prospect column.
   * Payload: `{ referral: EngageReferral }`.
   */
  EngageWorklistProspectSelectFromActivity = "engageWorklistProspectSelectFromActivity",
  /** Search preview modal overlay cache hydrated — listeners read {@link ApplicationCacheService.applicationCache}. */
  SearchPreviewCacheUpdated = "searchPreviewCacheUpdated",
  /** Global search patient preview opened on worklist prospect — main column must stay pinned. */
  SearchPatientPreviewSessionStarted = "searchPatientPreviewSessionStarted",
  /** Global search patient preview closed without expanding into worklist. */
  SearchPatientPreviewSessionEnded = "searchPatientPreviewSessionEnded",
  /** Payload: { contextId?: string, contextTitle?: string }. Scroll to that context in listing after modal save. */
  ScrollToContextInListing = "scrollToContextInListing",
}
